<script setup lang="ts">
import { computed, ref } from "vue";
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from "reka-ui";

import { $can } from "@/services/UtilitiesService";

import { useBrandSecurityStore, useBrandStore } from "@/stores";

import CaseDetailsAccess from "@/components/CasePanels/CaseDetails/Access/Access.vue";
import CaseDetailsRetention from "@/components/CasePanels/CaseDetails/Retention/Retention.vue";
import CaseDetailsMigration from "@/components/CasePanels/CaseDetails/Migration/Migration.vue";

import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

defineProps<DockviewPanelComponentProps<false>>();
defineEmits<DockviewPanelComponentEmits>();

const brandStore = useBrandStore();

const tabs = computed(() =>
  [
    { key: "retention", title: "Retention", show: brandStore.viewerConfig.retention?.showRetentionSection === true },
    {
      key: "access",
      title: "View/Manage Access",
      show:
        (useBrandSecurityStore().config?.isCaseSecurityEnabled ?? false) &&
        ($can("auditAccess") || $can("manageAccess")),
    },
    { key: "migration", title: "Migration", show: $can("migrateRecords") },
  ].filter((s) => s.show),
);

const migrationComponentKey = ref<number>(0);

const _selectedTab = ref<string | undefined>();
const selectedTab = computed({
  get: () => {
    if (_selectedTab.value == null || !tabs.value.some((tab) => tab.key === _selectedTab.value))
      return tabs.value[0]?.key;
    return _selectedTab.value;
  },
  set: (value) => {
    _selectedTab.value = value;
  },
});
</script>
<template>
  <div v-if="selectedTab == null" class="tw-grid tw-size-full tw-place-items-center">
    <div class="tw-max-w-sm tw-text-pretty">No accessible tabs</div>
  </div>
  <TabsRoot
    v-else
    v-model="selectedTab"
    class="tw-flex tw-h-full tw-flex-col tw-@container [&>*:last-child]:tw-min-h-0 [&>*:last-child]:tw-grow [&>*:last-child]:tw-overflow-auto"
  >
    <TabsList class="tw-mt-1 tw-border-b tw-px-2">
      <div class="tw-flex tw-gap-1 tw-overflow-x-auto">
        <template v-for="tab in tabs" :key="tab.key">
          <TabsTrigger
            v-if="tab.show"
            :key="`${tab.key}-${String(selectedTab === tab.key)}`"
            class="tw-min-w-max tw-rounded-t-md tw-border tw-border-b-0 tw-px-2 tw-py-1 hover:tw-brightness-90"
            :value="tab.key"
            :class="selectedTab === tab.key ? 'tw-bg-green' : 'tw-bg-white'"
          >
            {{ tab.title }}
          </TabsTrigger>
        </template>
      </div>
    </TabsList>
    <TabsContent as-child value="access">
      <CaseDetailsAccess />
    </TabsContent>
    <TabsContent as-child value="retention">
      <CaseDetailsRetention />
    </TabsContent>
    <TabsContent as-child value="migration">
      <CaseDetailsMigration :key="migrationComponentKey" @reset-component="() => migrationComponentKey++" />
    </TabsContent>
  </TabsRoot>
</template>
